import React, { useState, useEffect } from 'react';
import { T, FONTS } from '../../constants/theme.js';
import {
  fetchCorporateAccounts, createCorporateAccount, updateCorporateAccount, deactivateCorporateAccount,
  fetchCorporateTariffs, addCorporateTariff, updateCorporateTariff, deleteCorporateTariff,
  fetchRoomTypes, fetchCorporateAccountGuests, updateGuest,
} from '../../services/api.js';
import { useOfflineInvalidation } from '../../components/ui.jsx';
import CorporateSessions from './CorporateSessions.jsx';

const SERVICE_TYPES = [
  { id:'accommodation', label:'Accommodation',     icon:'🛏️' },
  { id:'restaurant',    label:'Restaurant',         icon:'🍽️' },
  { id:'gym',           label:'Gym',                icon:'🏋️' },
  { id:'swimming',      label:'Swimming Pool',      icon:'🏊' },
  { id:'conference',    label:'Conference Hall',    icon:'🏢' },
  { id:'laundry',       label:'Laundry',            icon:'👕' },
  { id:'transport',     label:'Airport Transfer',   icon:'🚌' },
  { id:'spa',           label:'Spa & Wellness',     icon:'💆' },
  { id:'minibar',       label:'Minibar',            icon:'🍷' },
  { id:'parking',       label:'Parking',            icon:'🚗' },
  { id:'phone',         label:'Phone / Internet',   icon:'📞' },
  { id:'other',         label:'Other Service',      icon:'📦' },
];
const serviceLabel = id => SERVICE_TYPES.find(s => s.id === id)?.label || id;
const serviceIcon  = id => SERVICE_TYPES.find(s => s.id === id)?.icon || '📦';

const EMPTY_ACCOUNT = {
  name:'', tin:'', contactName:'', contactEmail:'', contactPhone:'', billingAddress:'',
  contractStatus:'active', contractStartDate:'', contractEndDate:'', paymentTerms:'', notes:'',
  proformaTermsOverride:'', paymentRequestBodyOverride:'',
};
const EMPTY_TARIFF = {
  serviceType:'accommodation', roomTypeId:'', negotiatedPrice:'', currency:'', status:'active',
  effectiveDate:'', expiryDate:'',
};

function Field({ label, hint, required, children }) {
  return (
    <div style={{ marginBottom:12 }}>
      <label style={{ display:'block', fontSize:9, fontWeight:700, color:T.muted,
        letterSpacing:0.8, textTransform:'uppercase', marginBottom:4 }}>
        {label}{required && <span style={{ color:T.danger }}> *</span>}
      </label>
      {children}
      {hint && <p style={{ fontSize:10, color:T.muted, marginTop:3 }}>{hint}</p>}
    </div>
  );
}

const inputStyle = {
  width:'100%', border:`1px solid ${T.border}`, borderRadius:8, padding:'8px 10px',
  fontSize:12, fontFamily:FONTS.body, color:T.navy, outline:'none', boxSizing:'border-box',
};

const Input    = ({ value, onChange, type='text', placeholder='' }) => (
  <input type={type} value={value} placeholder={placeholder} onChange={e=>onChange(e.target.value)} style={inputStyle} />
);
const Select   = ({ value, onChange, children }) => (
  <select value={value} onChange={e=>onChange(e.target.value)} style={inputStyle}>{children}</select>
);
const TextArea = ({ value, onChange, placeholder='', rows=2 }) => (
  <textarea value={value} placeholder={placeholder} rows={rows} onChange={e=>onChange(e.target.value)}
    style={{ ...inputStyle, resize:'vertical' }} />
);

const STATUS_COLORS = {
  active:    { bg:T.successPale, text:T.success },
  inactive:  { bg:T.surface,     text:T.muted },
  expired:   { bg:T.dangerPale,  text:T.danger },
  suspended: { bg:T.warningPale, text:T.warning },
};

function StatusBadge({ status }) {
  const c = STATUS_COLORS[status] || STATUS_COLORS.inactive;
  return (
    <span style={{ fontSize:9, fontWeight:700, color:c.text, background:c.bg,
      padding:'3px 9px', borderRadius:99, textTransform:'uppercase', letterSpacing:.5 }}>
      {status || 'inactive'}
    </span>
  );
}

export default function CorporateAccounts({ property }) {
  const [accounts,   setAccounts]   = useState([]);
  const [roomTypes,  setRoomTypes]  = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [showForm,   setShowForm]   = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [editId,     setEditId]     = useState(null);
  const [form,       setForm]       = useState(EMPTY_ACCOUNT);
  const [saving,     setSaving]     = useState(false);
  const [toast,      setToast]      = useState('');
  const [selected,   setSelected]   = useState(null);     // account open for tariffs
  const [tariffs,    setTariffs]    = useState([]);
  const [tariffsLoading, setTariffsLoading] = useState(false);
  const [tariffForm, setTariffForm] = useState(EMPTY_TARIFF);
  const [editTariffId, setEditTariffId] = useState(null);
  const [guestsAccount,  setGuestsAccount]  = useState(null);
  const [linkedGuests,   setLinkedGuests]   = useState([]);
  const [guestsLoading,  setGuestsLoading]  = useState(false);
  // ── NEW: which account's sessions to show ──────────────────────────────
  const [selectedForSessions, setSelectedForSessions] = useState(null);

  const pid      = property?.id;
  const currency = property?.currency || 'USD';

  const offlineTick = useOfflineInvalidation('corporate-accounts');

  const load = async () => {
    if (!pid) return;
    setLoading(true);
    try {
      const [acc, rt] = await Promise.all([fetchCorporateAccounts(pid), fetchRoomTypes(pid)]);
      setAccounts(Array.isArray(acc) ? acc : []);
      setRoomTypes(Array.isArray(rt) ? rt : []);
    } catch { setAccounts([]); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [pid, offlineTick]); // eslint-disable-line

  const showToast = msg => { setToast(msg); setTimeout(() => setToast(''), 3000); };
  const set  = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const setT = (k, v) => setTariffForm(f => ({ ...f, [k]: v }));

  // ── If an account is selected for sessions, render CorporateSessions ───
  if (selectedForSessions) {
    return (
      <CorporateSessions
        account={selectedForSessions}
        property={property}
        onBack={() => setSelectedForSessions(null)}
      />
    );
  }

  const handleSave = async () => {
    if (!form.name.trim()) { showToast('Company name is required'); return; }
    setSaving(true);
    try {
      const payload = { ...form, propertyId: pid,
        contractStartDate: form.contractStartDate || null,
        contractEndDate:   form.contractEndDate   || null };
      if (editId) {
        const res = await updateCorporateAccount(editId, payload);
        showToast(res?._offline ? '⏳ Saved offline — will sync when server is back' : '✅ Company updated');
      } else {
        const res = await createCorporateAccount(payload);
        showToast(res?._offline ? '⏳ Saved offline — will sync when server is back' : '✅ Company added');
      }
      setShowForm(false); setEditId(null); setForm(EMPTY_ACCOUNT); load();
    } catch (e) { showToast(`❌ ${e.message}`); }
    finally { setSaving(false); }
  };

  const handleDeactivate = async (id, name) => {
    if (!window.confirm(`Deactivate "${name}"? Existing reservations are unaffected, but it won't be selectable for new corporate bookings.`)) return;
    try { await deactivateCorporateAccount(id); showToast(`✅ ${name} deactivated`); load(); }
    catch (e) { showToast(`❌ ${e.message}`); }
  };

  const openEdit = a => {
    setForm({
      name: a.name || '', tin: a.tin || '', contactName: a.contactName || '',
      contactEmail: a.contactEmail || '', contactPhone: a.contactPhone || '',
      billingAddress: a.billingAddress || '', contractStatus: a.contractStatus || 'active',
      contractStartDate: a.contractStartDate || '', contractEndDate: a.contractEndDate || '',
      paymentTerms: a.paymentTerms || '', notes: a.notes || '',
      proformaTermsOverride: a.proformaTermsOverride || '',
      paymentRequestBodyOverride: a.paymentRequestBodyOverride || '',
    });
    setEditId(a.id); setShowForm(true); setSelected(null); setGuestsAccount(null);
  };

  const openTariffs = async (account) => {
    setSelected(account); setTariffForm(EMPTY_TARIFF); setEditTariffId(null);
    setGuestsAccount(null);
    setTariffsLoading(true);
    try { setTariffs(await fetchCorporateTariffs(account.id)); }
    catch { setTariffs([]); }
    finally { setTariffsLoading(false); }
  };

  const openGuests = async (account) => {
    setGuestsAccount(account); setSelected(null);
    setGuestsLoading(true);
    try { setLinkedGuests(await fetchCorporateAccountGuests(account.id)); }
    catch { setLinkedGuests([]); }
    finally { setGuestsLoading(false); }
  };

  const handleUnlinkGuest = async (guest) => {
    if (!window.confirm(`Unlink ${guest.fullName || guest.firstName} from ${guestsAccount.name}?`)) return;
    try {
      await updateGuest(guest.id, { corporateAccountId: '' });
      setLinkedGuests(gs => gs.filter(g => g.id !== guest.id));
      showToast(`✅ ${guest.fullName || guest.firstName} unlinked`);
    } catch (e) { showToast(`❌ ${e.message}`); }
  };

  const handleSaveTariff = async () => {
    if (!tariffForm.negotiatedPrice) { showToast('Negotiated price is required'); return; }
    try {
      const payload = { ...tariffForm,
        negotiatedPrice: parseFloat(tariffForm.negotiatedPrice),
        currency: tariffForm.currency || currency,
        roomTypeId: tariffForm.serviceType === 'accommodation' ? (tariffForm.roomTypeId || null) : null,
        effectiveDate: tariffForm.effectiveDate || null,
        expiryDate: tariffForm.expiryDate || null,
      };
      if (editTariffId) await updateCorporateTariff(editTariffId, payload);
      else await addCorporateTariff(selected.id, payload);
      showToast('✅ Tariff saved');
      setTariffForm(EMPTY_TARIFF); setEditTariffId(null);
      setTariffs(await fetchCorporateTariffs(selected.id));
    } catch (e) { showToast(`❌ ${e.message}`); }
  };

  const handleDeleteTariff = async (tariffId) => {
    if (!window.confirm('Remove this tariff line?')) return;
    try {
      await deleteCorporateTariff(tariffId);
      setTariffs(await fetchCorporateTariffs(selected.id));
      showToast('✅ Tariff removed');
    } catch (e) { showToast(`❌ ${e.message}`); }
  };

  const editTariff = t => {
    setTariffForm({
      serviceType: t.serviceType, roomTypeId: t.roomTypeId || '',
      negotiatedPrice: t.negotiatedPrice?.toString() || '', currency: t.currency || '',
      status: t.status || 'active', effectiveDate: t.effectiveDate || '', expiryDate: t.expiryDate || '',
    });
    setEditTariffId(t.id);
  };

  return (
    <div className="fade-up">
      {toast && (
        <div style={{ position:'fixed', top:70, right:24, zIndex:999, background:T.navy,
          color:T.white, borderRadius:10, padding:'11px 18px', fontSize:13, fontWeight:600,
          boxShadow:'0 8px 24px rgba(0,0,0,0.2)' }}>{toast}</div>
      )}

      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
        <div>
          <h1 style={{ fontFamily:FONTS.display, fontSize:28, fontWeight:700, color:T.navy }}>Corporate Accounts</h1>
          <p style={{ fontSize:12, color:T.muted, marginTop:2 }}>
            {accounts.length} compan{accounts.length===1?'y':'ies'} · Each company can have multiple booking sessions
          </p>
        </div>
        <button onClick={() => { setForm(EMPTY_ACCOUNT); setEditId(null); setShowForm(s => !s); setSelected(null); setGuestsAccount(null); }}
          style={{ background:T.navy, border:'none', borderRadius:10, padding:'10px 20px',
            fontSize:13, fontWeight:700, color:T.white, cursor:'pointer', fontFamily:FONTS.body }}>
          + New Company
        </button>
      </div>

      {showForm && (
        <div style={{ background:T.tealPale, border:`1px solid ${T.teal}30`, borderRadius:14,
          padding:22, marginBottom:20 }}>
          <p style={{ fontSize:13, fontWeight:700, color:T.navy, marginBottom:16 }}>
            {editId ? 'Edit Company' : 'New Company'}
          </p>
          <div style={{ display:'grid', gridTemplateColumns:'2fr 1fr 1fr', gap:14 }}>
            <Field label="Company / Organization Name" required>
              <Input value={form.name} onChange={v=>set('name',v)} placeholder="World Relief" />
            </Field>
            <Field label="TIN"><Input value={form.tin} onChange={v=>set('tin',v)} placeholder="103420905" /></Field>
            <Field label="Contract Status">
              <Select value={form.contractStatus} onChange={v=>set('contractStatus',v)}>
                {['active','inactive','expired','suspended'].map(s=><option key={s} value={s}>{s}</option>)}
              </Select>
            </Field>
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:14 }}>
            <Field label="Contact Name"><Input value={form.contactName} onChange={v=>set('contactName',v)} /></Field>
            <Field label="Contact Email"><Input type="email" value={form.contactEmail} onChange={v=>set('contactEmail',v)} /></Field>
            <Field label="Contact Phone"><Input value={form.contactPhone} onChange={v=>set('contactPhone',v)} /></Field>
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:14 }}>
            <Field label="Billing Address"><Input value={form.billingAddress} onChange={v=>set('billingAddress',v)} /></Field>
            <Field label="Contract Start"><Input type="date" value={form.contractStartDate} onChange={v=>set('contractStartDate',v)} /></Field>
            <Field label="Contract End"><Input type="date" value={form.contractEndDate} onChange={v=>set('contractEndDate',v)} /></Field>
          </div>
          <Field label="Payment Terms" hint="Default terms shown on documents. Can be overridden per session.">
            <TextArea value={form.paymentTerms} onChange={v=>set('paymentTerms',v)} placeholder="Net 30 / 50% deposit required..." />
          </Field>
          <Field label="Internal Notes"><TextArea value={form.notes} onChange={v=>set('notes',v)} rows={2} /></Field>

          <button type="button" onClick={() => setShowAdvanced(s => !s)}
            style={{ background:'transparent', border:'none', cursor:'pointer', fontSize:11,
              fontWeight:700, color:T.teal, padding:0, marginBottom:showAdvanced?12:4 }}>
            {showAdvanced ? '▾' : '▸'} Advanced: Document Template Overrides
          </button>
          {showAdvanced && (
            <div style={{ background:T.white, border:`1px solid ${T.border}`, borderRadius:10, padding:14, marginBottom:12 }}>
              <p style={{ fontSize:10.5, color:T.muted, marginBottom:10, lineHeight:1.5 }}>
                Optional — overrides the property's default wording on the Proforma Invoice / Payment Request
                specifically for this company. Leave blank to use the property default.
              </p>
              <Field label="Proforma Invoice — Terms & Conditions Override">
                <TextArea value={form.proformaTermsOverride} onChange={v=>set('proformaTermsOverride',v)} rows={3}
                  placeholder="Leave blank to use the property default" />
              </Field>
              <Field label="Payment Request — Body Paragraph Override" hint={
                <>Merge fields: <code>{'{company}'}</code> <code>{'{property}'}</code> <code>{'{amountWords}'}</code> <code>{'{amountNumeric}'}</code> <code>{'{currency}'}</code></>
              }>
                <TextArea value={form.paymentRequestBodyOverride} onChange={v=>set('paymentRequestBodyOverride',v)} rows={3}
                  placeholder="Leave blank to use the property default" />
              </Field>
            </div>
          )}
          <div style={{ display:'flex', gap:10, marginTop:4 }}>
            <button onClick={() => { setShowForm(false); setEditId(null); setForm(EMPTY_ACCOUNT); }}
              style={{ background:T.white, border:`1px solid ${T.border}`, borderRadius:9, padding:'9px 18px',
                fontSize:12, cursor:'pointer', fontFamily:FONTS.body, color:T.muted }}>Cancel</button>
            <button onClick={handleSave} disabled={saving}
              style={{ background:saving?T.muted:T.teal, border:'none', borderRadius:9, padding:'9px 24px',
                fontSize:12, fontWeight:700, cursor:saving?'not-allowed':'pointer',
                fontFamily:FONTS.body, color:T.white }}>
              {saving ? 'Saving…' : editId ? 'Save Changes' : 'Create Company'}
            </button>
          </div>
        </div>
      )}

      {/* ── Account-level tariffs panel ─────────────────────────────────────── */}
      {selected && (
        <div style={{ background:T.white, border:`1px solid ${T.border}`, borderRadius:14,
          padding:22, marginBottom:20, boxShadow:'0 4px 16px rgba(0,0,0,.06)' }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
            <div>
              <p style={{ fontSize:15, fontWeight:700, color:T.navy }}>🏢 {selected.name} — Negotiated Tariffs</p>
              <p style={{ fontSize:11, color:T.muted, marginTop:2 }}>
                Account-level default rates. Each Session can override these with its own agreed tariffs.
              </p>
            </div>
            <button onClick={() => setSelected(null)}
              style={{ background:'transparent', border:'none', fontSize:13, color:T.muted, cursor:'pointer' }}>✕ Close</button>
          </div>

          {tariffsLoading ? (
            <p style={{ fontSize:12, color:T.muted }}>Loading tariffs…</p>
          ) : tariffs.length === 0 ? (
            <p style={{ fontSize:12, color:T.muted, marginBottom:16 }}>No tariffs configured yet.</p>
          ) : (
            <div style={{ display:'flex', flexDirection:'column', gap:8, marginBottom:18 }}>
              {tariffs.map(t => (
                <div key={t.id} style={{ display:'flex', alignItems:'center', justifyContent:'space-between',
                  background:T.surface, border:`1px solid ${T.border}`, borderRadius:10, padding:'10px 14px' }}>
                  <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                    <span style={{ fontSize:16 }}>{serviceIcon(t.serviceType)}</span>
                    <div>
                      <p style={{ fontSize:12, fontWeight:700, color:T.navy }}>
                        {serviceLabel(t.serviceType)}
                        {t.roomTypeId && (
                          <span style={{ fontWeight:500, color:T.muted }}>
                            {' '}— {roomTypes.find(rt=>rt.id===t.roomTypeId)?.name || 'room type'}
                          </span>
                        )}
                      </p>
                      <p style={{ fontSize:10, color:T.muted }}>{t.effectiveDate || '—'} → {t.expiryDate || '∞'}</p>
                    </div>
                  </div>
                  <div style={{ display:'flex', alignItems:'center', gap:14 }}>
                    <p style={{ fontSize:14, fontWeight:700, color:T.navy }}>
                      {t.currency || currency} {parseFloat(t.negotiatedPrice||0).toLocaleString()}
                    </p>
                    <StatusBadge status={t.status} />
                    <button onClick={() => editTariff(t)}
                      style={{ background:'transparent', border:'none', cursor:'pointer', fontSize:13 }}>✏️</button>
                    <button onClick={() => handleDeleteTariff(t.id)}
                      style={{ background:'transparent', border:'none', cursor:'pointer', fontSize:13 }}>🗑️</button>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div style={{ background:T.tealPale, borderRadius:12, padding:16 }}>
            <p style={{ fontSize:11, fontWeight:700, color:T.navy, marginBottom:10, textTransform:'uppercase', letterSpacing:.6 }}>
              {editTariffId ? 'Edit Tariff' : 'Add Tariff'}
            </p>
            <div style={{ display:'grid', gridTemplateColumns: tariffForm.serviceType==='accommodation' ? '1fr 1fr 1fr 1fr' : '1fr 1fr 1fr', gap:12 }}>
              <Field label="Service Type">
                <Select value={tariffForm.serviceType} onChange={v=>setT('serviceType',v)}>
                  {SERVICE_TYPES.map(s=><option key={s.id} value={s.id}>{s.icon} {s.label}</option>)}
                </Select>
              </Field>
              {tariffForm.serviceType === 'accommodation' && (
                <Field label="Room Type" hint="Blank = any room type">
                  <Select value={tariffForm.roomTypeId} onChange={v=>setT('roomTypeId',v)}>
                    <option value="">— Any room type —</option>
                    {roomTypes.map(rt=><option key={rt.id} value={rt.id}>{rt.name}</option>)}
                  </Select>
                </Field>
              )}
              <Field label={`Negotiated Price (${currency})`} required>
                <Input type="number" min="0" value={tariffForm.negotiatedPrice} onChange={v=>setT('negotiatedPrice',v)} placeholder="0.00" />
              </Field>
              <Field label="Status">
                <Select value={tariffForm.status} onChange={v=>setT('status',v)}>
                  <option value="active">active</option>
                  <option value="inactive">inactive</option>
                </Select>
              </Field>
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
              <Field label="Effective Date"><Input type="date" value={tariffForm.effectiveDate} onChange={v=>setT('effectiveDate',v)} /></Field>
              <Field label="Expiry Date (optional)"><Input type="date" value={tariffForm.expiryDate} onChange={v=>setT('expiryDate',v)} /></Field>
            </div>
            <div style={{ display:'flex', gap:10 }}>
              {editTariffId && (
                <button onClick={() => { setTariffForm(EMPTY_TARIFF); setEditTariffId(null); }}
                  style={{ background:T.white, border:`1px solid ${T.border}`, borderRadius:9, padding:'8px 16px',
                    fontSize:12, cursor:'pointer', fontFamily:FONTS.body, color:T.muted }}>Cancel Edit</button>
              )}
              <button onClick={handleSaveTariff}
                style={{ background:T.teal, border:'none', borderRadius:9, padding:'8px 20px',
                  fontSize:12, fontWeight:700, cursor:'pointer', fontFamily:FONTS.body, color:T.white }}>
                {editTariffId ? 'Save Tariff' : '+ Add Tariff'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Linked guests panel ─────────────────────────────────────────────── */}
      {guestsAccount && (
        <div style={{ background:T.white, border:`1px solid ${T.border}`, borderRadius:14,
          padding:22, marginBottom:20, boxShadow:'0 4px 16px rgba(0,0,0,.06)' }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
            <div>
              <p style={{ fontSize:15, fontWeight:700, color:T.navy }}>👥 {guestsAccount.name} — Linked Guests</p>
              <p style={{ fontSize:11, color:T.muted, marginTop:2 }}>
                Company-wide guest association. Session-specific guest enrollment is managed inside each Session.
              </p>
            </div>
            <button onClick={() => setGuestsAccount(null)}
              style={{ background:'transparent', border:'none', fontSize:13, color:T.muted, cursor:'pointer' }}>✕ Close</button>
          </div>

          {guestsLoading ? (
            <p style={{ fontSize:12, color:T.muted }}>Loading guests…</p>
          ) : linkedGuests.length === 0 ? (
            <p style={{ fontSize:12, color:T.muted }}>
              No guests linked yet. Open a guest's profile in Guest Management and set "Linked Corporate Account" to {guestsAccount.name}.
            </p>
          ) : (
            <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
              {linkedGuests.map(g => (
                <div key={g.id} style={{ display:'flex', alignItems:'center', justifyContent:'space-between',
                  background:T.surface, border:`1px solid ${T.border}`, borderRadius:10, padding:'10px 14px' }}>
                  <div>
                    <p style={{ fontSize:12, fontWeight:700, color:T.navy }}>{g.fullName || `${g.firstName} ${g.lastName}`}</p>
                    <p style={{ fontSize:10.5, color:T.muted }}>{g.email || '—'} · {g.phone || '—'}</p>
                  </div>
                  <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                    <span style={{ fontSize:10, color:T.muted }}>{g.visitCount || 0} visit{g.visitCount===1?'':'s'}</span>
                    <button onClick={() => handleUnlinkGuest(g)}
                      style={{ background:'transparent', border:`1px solid ${T.danger}30`, borderRadius:7,
                        padding:'4px 10px', fontSize:10, fontWeight:700, cursor:'pointer',
                        fontFamily:FONTS.body, color:T.danger }}>
                      Unlink
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Account cards list ──────────────────────────────────────────────── */}
      {loading ? (
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(320px,1fr))', gap:14 }}>
          {Array.from({length:4}).map((_,i) => (
            <div key={i} style={{ height:180, borderRadius:14, background:T.border }} className="shimmer" />
          ))}
        </div>
      ) : accounts.length === 0 ? (
        <div style={{ background:T.surface, border:`2px dashed ${T.border}`, borderRadius:14,
          padding:40, textAlign:'center' }}>
          <p style={{ fontSize:32, marginBottom:12 }}>🏢</p>
          <p style={{ fontSize:14, fontWeight:600, color:T.slate, marginBottom:4 }}>No corporate accounts yet</p>
          <p style={{ fontSize:12, color:T.muted }}>Add a company to start creating corporate booking sessions</p>
        </div>
      ) : (
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(320px,1fr))', gap:14 }}>
          {accounts.map(a => (
            <div key={a.id} style={{ background:T.white, border:`1px solid ${T.border}`,
              borderRadius:14, overflow:'hidden' }}>
              <div style={{ height:3, background:`linear-gradient(90deg,${T.teal},${T.teal}44)` }} />
              <div style={{ padding:18 }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:8 }}>
                  <p style={{ fontSize:14, fontWeight:700, color:T.navy }}>{a.name}</p>
                  <StatusBadge status={a.contractStatus} />
                </div>
                {a.tin && <p style={{ fontSize:10, color:T.muted, marginBottom:4 }}>TIN: {a.tin}</p>}
                {(a.contractStartDate || a.contractEndDate) && (
                  <p style={{ fontSize:10, color:T.muted, marginBottom:4 }}>
                    📅 {a.contractStartDate || '—'} → {a.contractEndDate || '∞'}
                  </p>
                )}
                {a.contactName && <p style={{ fontSize:11, color:T.slate, marginBottom:10 }}>👤 {a.contactName}</p>}

                {/* ── Action buttons — 4 across ─── */}
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr 1fr', gap:6, marginTop:10 }}>
                  {/* Sessions — primary action (tariffs moved to Session level) */}
                  <button
                    onClick={() => { setSelected(null); setGuestsAccount(null); setShowForm(false); setSelectedForSessions(a); }}
                    style={{ gridColumn:'1 / -1', background:T.navy, border:'none',
                      borderRadius:8, padding:'9px 0', fontSize:12, cursor:'pointer',
                      fontFamily:FONTS.body, color:T.white, fontWeight:700,
                      display:'flex', alignItems:'center', justifyContent:'center', gap:6 }}>
                    📁 Sessions
                    <span style={{ fontSize:10, fontWeight:600, opacity:.75 }}>({a.sessionCount || 0})</span>
                  </button>
                  <button onClick={() => { setSelected(null); openGuests(a); }}
                    style={{ gridColumn:'1 / -1', background:T.tealPale, border:`1px solid ${T.teal}30`,
                      borderRadius:8, padding:'7px 0', fontSize:11, cursor:'pointer',
                      fontFamily:FONTS.body, color:T.teal, fontWeight:700,
                      display:'flex', alignItems:'center', justifyContent:'center', gap:6 }}>
                    👥 Guests
                    <span style={{ fontSize:10, opacity:.75 }}>({a.guestCount || 0})</span>
                  </button>
                  <button onClick={() => { setSelected(null); setGuestsAccount(null); openEdit(a); }}
                    style={{ background:T.surface, border:`1px solid ${T.border}`,
                      borderRadius:8, padding:'7px 0', fontSize:11, cursor:'pointer',
                      fontFamily:FONTS.body, color:T.slate, fontWeight:600 }}>
                    ✏️ Edit
                  </button>
                  <button onClick={() => handleDeactivate(a.id, a.name)}
                    style={{ background:T.dangerPale, border:`1px solid ${T.danger}30`,
                      borderRadius:8, padding:'7px 0', fontSize:11, cursor:'pointer',
                      fontFamily:FONTS.body, color:T.danger, fontWeight:600 }}>
                    Off
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
